import { useState, useMemo, useCallback } from "react"
import { Chart, TableVisualization } from "~/ui-rtk/components/ui/charts"
import { DateRangePicker, MultiSelect } from "~/ui-rtk/components/ui/controls"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { usePeriod, DATE_FORMAT, isValidDate } from "~/ui-rtk/hooks/period"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import type { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import LocationFilter from "./components/LocationFilter"
import useDate from "~/ui-rtk/hooks/date"
import {
  VISUALIZATION_TYPE,
  VISUALIZATIONS_MAP,
} from "~/ui-rtk/constants/charts"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import useUrlColumnKeys from "~/ui-rtk/hooks/url-column-keys"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import type { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import useSources from "~/ui-rtk/hooks/sources"
import TabsGroup from "~/ui-rtk/components/ui/controls/TabsGroup/TabsGroup"

const dayjs = useDate()

const TAB_CONFIG = [
  {
    label: "SKU Sell-Outs",
    widgetId: "company_shopify_inventory_sku_sellouts_table",
    urlPrefix: "inv_sku_sellouts",
    nameDimension: "sku",
    sortKey: "company_shopify_variant_daily_sales.units_sold",
    locationDimension: "company_shopify_inventory_events.location_id",
  },
  {
    label: "Product Breakage",
    widgetId: "company_shopify_inventory_product_breakage_table",
    urlPrefix: "inv_product_breakage",
    nameDimension: "product_title",
    sortKey: "company_shopify_product_variant_breakage.broken_variant_count",
    locationDimension: "company_shopify_product_variant_breakage.location_id",
  },
] as const

const TAB_OPTIONS = TAB_CONFIG.map(({ label }) => ({ label }))

export default function InventoryAnalysisPage() {
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const { dateRange, compareRange } = useMinMaxDateRange()
  const { granularity, setPeriod, setComparePeriod } = usePeriod()
  const { sources } = useSources()

  const activeTab = TAB_CONFIG[activeTabIndex]
  const tableProps = useMemo(
    () => VISUALIZATIONS_MAP[activeTab.widgetId]?.props,
    [activeTab.widgetId],
  )

  // Primary-location filter, persisted in the URL so it survives reload and is
  // shareable. Applied as an exact-match (equals) cube filter on the active
  // tab's location dimension — fast, unlike a "contains" scan on the id.
  const { params, setParams } = useURLSearchParams()
  const locationId = params.inv_location_id ?? ""
  const setLocationId = useCallback(
    (value: string) => setParams({ inv_location_id: value || undefined }),
    [setParams],
  )

  const cubeFilters = useMemo<TCubeFilterOptions | undefined>(() => {
    if (!locationId) return undefined
    return {
      and: [
        {
          member: activeTab.locationDimension,
          operator: "equals",
          values: [locationId],
        },
      ],
    }
  }, [locationId, activeTab.locationDimension])

  const defaultColumnKeys = useMemo(
    () =>
      tableProps?.columnConfig
        .filter(
          (column: TableVisualizationColumn & { disabled?: boolean }) =>
            !column.disabled,
        )
        .map((column: TableVisualizationColumn) => column.key) || [],
    [tableProps],
  )

  const { columnKeys, setColumnKeys } = useUrlColumnKeys(
    activeTab.urlPrefix,
    defaultColumnKeys,
  )

  const columnOptions = useMemo(() => {
    const options = tableProps?.columnConfig.map(
      (column: TableVisualizationColumn) =>
        ({
          value: column.key,
          label: column.header,
        }) as TMultiSelectOption,
    )
    return options || []
  }, [tableProps])

  const columnConfigFiltered = useMemo(
    () =>
      (tableProps?.columnConfig ?? [])
        .filter(({ key }: { key: string }) =>
          (columnKeys as string[])?.includes(key),
        )
        .map(
          ({
            disabled,
            ...col
          }: TableVisualizationColumn & { disabled?: boolean }) => col,
        ),
    [tableProps?.columnConfig, columnKeys],
  )

  const changePeriod = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )

    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  return (
    <div className="px-4">
      <div className="flex items-center justify-between">
        <Breadcrumbs
          items={[
            {
              name: "Web Merchandising",
              slug: brandRoutes.WEB_MERCHANDISING.ROOT,
            },
            {
              name: "Inventory Analysis",
              slug: brandRoutes.WEB_MERCHANDISING.INVENTORY_ANALYSIS,
            },
          ]}
        />
      </div>

      <div className="mt-system-lg mb-system-md">
        <TabsGroup
          options={TAB_OPTIONS}
          index={activeTabIndex}
          onChange={setActiveTabIndex}
        />
      </div>

      <div className="flex justify-end items-stretch gap-system-sm mb-system-lg">
        <LocationFilter value={locationId} onChange={setLocationId} />
        <MultiSelect
          label="Columns"
          options={columnOptions as TMultiSelectOption[]}
          value={columnOptions.filter((option: TMultiSelectOption) =>
            (columnKeys as string[]).includes(option.value),
          )}
          onChange={(options: TMultiSelectOption[]) =>
            setColumnKeys(options.map(({ value }) => value))
          }
        />
        <TableFiltersBar
          id={activeTab.urlPrefix}
          columnConfig={columnConfigFiltered}
        />
        {dateRange?.[0] &&
          dateRange?.[1] &&
          isValidDate(dateRange[0]) &&
          isValidDate(dateRange[1]) && (
            <DateRangePicker
              start={dateRange[0]}
              end={dateRange[1]}
              compareStart={
                compareRange?.[0] && isValidDate(compareRange[0])
                  ? compareRange[0]
                  : undefined
              }
              compareEnd={
                compareRange?.[1] && isValidDate(compareRange[1])
                  ? compareRange[1]
                  : undefined
              }
              comparisonEnabled={false}
              onChange={changePeriod}
            />
          )}
      </div>

      <div className="mt-3">
        <Chart
          {...tableProps}
          chartId={activeTab.widgetId}
          urlParamsPrefix={activeTab.urlPrefix}
          className=""
          widgetType={VISUALIZATION_TYPE.TableVisualization}
          component={TableVisualization}
          cubeFilters={cubeFilters}
          dateRange={dateRange}
          compareRange={compareRange ?? undefined}
          comparisonEnabled={false}
          granularity={granularity}
          sources={sources}
          hideAggSelector={true}
          columnConfig={columnConfigFiltered}
          enableCSVExport={true}
          csvExportFilename={activeTab.urlPrefix}
          defaultSorting={[
            {
              id: activeTab.sortKey,
              desc: true,
            },
          ]}
        />
      </div>
    </div>
  )
}
